import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  listSipAccountsQueryKey,
  listTelephonyPoolsOptions,
  listTelephonyProfilesOptions,
  registerSipAccountMutation,
  updateSipAccountMutation,
  useLineLeases,
  useSipAccount,
} from '@/entities/telephony'
import { errorMessage } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { SelectField, TextareaField, TextField } from '@/shared/ui/field'
import { SlideOver } from '@/shared/ui/slide-over'

interface AccountFormProps {
  open: boolean
  // id учётки переводит форму в режим правки; без него — заведение новой
  accountId?: string
  onClose: () => void
}

// Аренда держит учётку, пока не слита: registering/active/draining — живые.
const LIVE_LEASE = new Set(['registering', 'active', 'draining'])

export function AccountForm({ open, accountId, onClose }: AccountFormProps) {
  const qc = useQueryClient()
  const mode = accountId ? 'edit' : 'create'
  const { account } = useSipAccount(accountId ?? '')
  const { leases } = useLineLeases({ accountId: accountId ?? '' })

  const liveLeases = accountId ? leases.filter((l) => LIVE_LEASE.has(l.status)) : []
  const leasedLines = liveLeases.reduce((acc, l) => acc + l.lines_count, 0)
  const locked = liveLeases.length > 0
  const disabled = account?.status === 'disabled'

  const profiles = useQuery({ ...listTelephonyProfilesOptions(), enabled: open })
  const pools = useQuery({ ...listTelephonyPoolsOptions(), enabled: open })

  const [domainHost, domainPort] = (account?.domain_override ?? ':').split(':')
  const [form, setForm] = useState({
    profileId: account?.provider_profile_id ?? '',
    poolId: account?.pool_id ?? '',
    ownerType: account?.owner_type ?? 'platform',
    ownerAccountId: account?.owner_account_id ?? '',
    username: account?.username ?? '',
    password: '',
    domainHost: domainHost ?? '',
    domainPort: domainPort || '5060',
    channels: account ? String(account.channels) : '',
    dids: '',
  })
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }))

  const onDone = () => {
    void qc.invalidateQueries({ queryKey: listSipAccountsQueryKey() })
    close()
  }
  const register = useMutation({ ...registerSipAccountMutation(), onSuccess: onDone })
  const update = useMutation({ ...updateSipAccountMutation(), onSuccess: onDone })
  const pending = register.isPending || update.isPending
  const error = register.error ?? update.error

  const close = () => {
    register.reset()
    update.reset()
    onClose()
  }

  const parseDids = () =>
    form.dids
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [e164, category] = line.split(';').map((p) => p.trim())
        return { e164: e164 ?? '', ...(category ? { marking_category: category } : {}) }
      })

  const domainBody = form.domainHost.trim()
    ? { domain_host: form.domainHost.trim(), domain_port: Number(form.domainPort) || 5060 }
    : {}

  const submit = () => {
    if (mode === 'create') {
      register.mutate({
        body: {
          provider_profile_id: form.profileId,
          ...(form.poolId ? { pool_id: form.poolId } : {}),
          owner_type: form.ownerType as 'platform' | 'client',
          ...(form.ownerAccountId.trim() ? { owner_account_id: form.ownerAccountId.trim() } : {}),
          ...(form.username.trim() ? { username: form.username.trim() } : {}),
          ...(form.password ? { password: form.password } : {}),
          ...domainBody,
          ...(form.channels ? { channels: Number(form.channels) } : {}),
          ...(parseDids().length > 0 ? { dids: parseDids() } : {}),
        },
      })
      return
    }
    if (!accountId) return
    update.mutate({
      path: { id: accountId },
      body: {
        pool_id: form.poolId,
        channels: Number(form.channels),
        // Пустой пароль ротацию не запускает, пустой domain_host снимает override.
        ...(form.password ? { password: form.password } : {}),
        ...domainBody,
      },
    })
  }

  const channels = Number(form.channels)
  const channelsTooLow = locked && channels < leasedLines
  const valid =
    mode === 'create'
      ? Boolean(form.profileId) &&
        (form.ownerType !== 'client' || form.ownerAccountId.trim() !== '')
      : !disabled && Boolean(form.poolId) && channels >= 1 && !channelsTooLow

  return (
    <SlideOver
      open={open}
      onClose={close}
      title={mode === 'create' ? 'Новая SIP-учётка' : `Учётка ${account?.username ?? ''}`}
      footer={
        <>
          <Button onClick={close}>Отмена</Button>
          <Button variant="primary" pending={pending} disabled={!valid} onClick={submit}>
            {mode === 'create' ? 'Завести и проверить' : 'Сохранить'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {disabled && (
          <p className="rounded-control bg-warn/10 px-4 py-2.5 text-body-sm text-warn">
            Учётка отключена — бэкенд правку отклонит. Сначала верни её в работу.
          </p>
        )}
        {locked && !disabled && (
          <p className="rounded-control bg-warn/10 px-4 py-2.5 text-body-sm text-warn">
            Учётку держит живая аренда ({leasedLines} линий): двигается только ёмкость и не ниже
            арендованного. Пул, пароль и домен — после слива аренды.
          </p>
        )}

        {mode === 'create' && (
          <SelectField
            label="Профиль оператора"
            required
            value={form.profileId}
            onChange={(e) => set({ profileId: e.target.value })}
          >
            <option value="" disabled>
              Выбери профиль
            </option>
            {(profiles.data?.data ?? [])
              .filter((p) => !p.retired)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.id}
                </option>
              ))}
          </SelectField>
        )}

        <SelectField
          label="Пул"
          required={mode === 'edit'}
          disabled={locked || disabled}
          value={form.poolId}
          onChange={(e) => set({ poolId: e.target.value })}
          hint={mode === 'edit' ? 'Учётка вне пула в маршрутизацию не попадает' : undefined}
        >
          {mode === 'create' ? (
            <option value="">Без пула</option>
          ) : (
            <option value="" disabled>
              Выбери пул
            </option>
          )}
          {(pools.data?.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} · {p.id}
            </option>
          ))}
        </SelectField>

        {mode === 'create' && (
          <>
            <SelectField
              label="Владелец"
              value={form.ownerType}
              onChange={(e) => set({ ownerType: e.target.value })}
            >
              <option value="platform">Платформа</option>
              <option value="client">Клиент (BYOC)</option>
            </SelectField>
            {form.ownerType === 'client' && (
              <TextField
                label="Аккаунт клиента"
                required
                placeholder="client-204"
                value={form.ownerAccountId}
                onChange={(e) => set({ ownerAccountId: e.target.value })}
              />
            )}
          </>
        )}

        <div className="grid grid-cols-2 gap-3">
          {mode === 'create' ? (
            <TextField
              label="Логин"
              placeholder="автогенерация"
              value={form.username}
              onChange={(e) => set({ username: e.target.value })}
            />
          ) : (
            <TextField label="Логин" value={account?.username ?? ''} disabled readOnly />
          )}
          <TextField
            label="Пароль"
            type="password"
            autoComplete="new-password"
            disabled={locked || disabled}
            placeholder={mode === 'create' ? 'автогенерация' : 'не менять'}
            value={form.password}
            onChange={(e) => set({ password: e.target.value })}
            hint={mode === 'edit' ? 'Смена уводит учётку в verifying' : undefined}
          />
        </div>

        <div className="grid grid-cols-[1fr_110px] gap-3">
          <TextField
            label="Domain override"
            placeholder="sip.custom.ru"
            disabled={locked || disabled}
            value={form.domainHost}
            onChange={(e) => set({ domainHost: e.target.value })}
            hint="Пусто — override снят, домен берётся из профиля"
          />
          <TextField
            label="Порт"
            type="number"
            disabled={locked || disabled}
            value={form.domainPort}
            onChange={(e) => set({ domainPort: e.target.value })}
          />
        </div>

        <TextField
          label="Каналы"
          type="number"
          required={mode === 'edit'}
          min={mode === 'edit' ? Math.max(1, leasedLines) : 0}
          disabled={disabled}
          placeholder="из профиля"
          value={form.channels}
          onChange={(e) => set({ channels: e.target.value })}
          error={channelsTooLow ? `Не ниже арендованных линий: ${leasedLines}` : undefined}
          hint={mode === 'edit' && !locked ? 'Ёмкость учётки в одновременных вызовах' : undefined}
        />

        {mode === 'create' && (
          <TextareaField
            label="DID-номера"
            placeholder={'+79001234567;доставка\n+79007654321'}
            value={form.dids}
            onChange={(e) => set({ dids: e.target.value })}
            hint="По одному на строку: e164;категория маркировки (категория опциональна)"
          />
        )}

        {error != null && (
          <p className="rounded-control bg-danger/10 px-4 py-2.5 text-body-sm text-danger">
            {errorMessage(error)}
          </p>
        )}
      </div>
    </SlideOver>
  )
}
